import DateFormats from '@/core/constants/DateFormats';
import { InvitationType } from '@/core/constants/invitation/type';
import { PlannerPolicy } from '@/core/policies/planner/PlannerPolicy';
import { ProspectPolicy } from '@/core/policies/prospect/ProspectPolicy';
import { SchedulePolicy } from '@/core/policies/schedule/SchedulePolicy';
import { getCustomerInfo } from '@/repositories/Customer';
import {
  getUserCustomers,
  getUserRelatedUsers,
} from '@/repositories/Hierarchy';
import { createInvitation, revokeInvitation } from '@/repositories/Invitation';
import { getUserPlannerEvents } from '@/repositories/Planner';
import {
  createUserProspect,
  deleteUserProspect,
  getUserProspects,
  updateUserProspect,
} from '@/repositories/Prospect';
import {
  addEventCommentEvidence,
  createEventAction,
  createEventComment,
  createEventExecution,
  createUserEvent,
  deleteEventComment,
  deleteEventCommentEvidence,
  getEventTypes,
  getUserEvents,
  updateEventComment,
  updateEventExecution,
  updateUserEvent,
} from '@/repositories/Schedule';
import {
  enrichEventsWithCustomerNames,
  matchEvent,
} from '@/utilities/schedule/event';
import moment from 'moment';

export default {
  namespaced: true,
  state: {
    events: [],
    plannerEvents: [],
    eventTypes: [],
    eventHistory: [],
    prospects: [],
    customers: [],
    relatedUsers: [],
    selectedUserId: null,
    selectedChannel: null,
    currentRange: { from: null, to: null },
    isLoadingEvents: false,
    isLoadingPlannerEvents: false,
    isLoadingEventTypes: false,
    isLoadingEventHistory: false,
    isLoadingProspects: false,
    isLoadingCustomers: false,
    isLoadingRelatedUsers: false,
  },
  getters: {
    events: (state) => state.events,
    plannerEvents: (state) => state.plannerEvents,
    eventTypes: (state) => state.eventTypes,
    eventHistory: (state) => state.eventHistory,
    prospects: (state) => state.prospects,
    customers: (state) => state.customers,
    relatedUsers: (state) => state.relatedUsers,
    selectedUserId: (state) => state.selectedUserId,
    selectedChannel: (state) => state.selectedChannel,
    currentRange: (state) => state.currentRange,
    isLoadingEvents: (state) => state.isLoadingEvents,
    isLoadingPlannerEvents: (state) => state.isLoadingPlannerEvents,
    isLoadingEventTypes: (state) => state.isLoadingEventTypes,
    isLoadingEventHistory: (state) => state.isLoadingEventHistory,
    isLoadingProspects: (state) => state.isLoadingProspects,
    isLoadingCustomers: (state) => state.isLoadingCustomers,
    eventsByCustomerAndDate: (state) => (customerCode, date) => {
      return state.events.filter(
        (event) =>
          String(event.customer?.code) === String(customerCode) &&
          moment(event.startDate).format(DateFormats.ISO_DATE) === date
      );
    },
    isReadOnly(state, getters, rootState, rootGetters) {
      return state.selectedUserId !== rootGetters['auth/user']?.id;
    },
    canCreateEvent: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['auth/user'];
      return SchedulePolicy.canCreate() && user?.id === state.selectedUserId;
    },
    canUpdateEvent: () => (event) => {
      return SchedulePolicy.canUpdate() && !event?.execution;
    },
    canUpdateEventExecution: () => (event) => {
      return (
        SchedulePolicy.canUpdate() &&
        event?.execution &&
        !event?.execution?.closed
      );
    },
    canCreateProspect: (state, getters, rootState, rootGetters) => {
      const user = rootGetters['auth/user'];
      return user?.id === state.selectedUserId && ProspectPolicy.canCreate();
    },
    canUpdateProspect: () => (prospect) => {
      return ProspectPolicy.canUpdate(prospect);
    },
    canDeleteProspect: () => (prospect) => {
      return ProspectPolicy.canDelete(prospect);
    },
  },
  mutations: {
    RESET_STORE(state) {
      state.events = [];
      state.plannerEvents = [];
      state.eventTypes = [];
      state.eventHistory = [];
      state.prospects = [];
      state.customers = [];
      state.relatedUsers = [];
      state.selectedUserId = null;
      state.selectedChannel = null;
      state.currentRange = { from: null, to: null };
      state.isloadingEvents = false;
      state.isLoadingPlannerEvents = false;
      state.isLoadingEventTypes = false;
      state.isLoadingEventHistory = false;
      state.isLoadingProspects = false;
      state.isLoadingCustomers = false;
      state.isLoadingRelatedUsers = false;
    },
    SET_EVENTS(state, events) {
      state.events = events;
    },
    CLEAR_EVENTS(state) {
      state.events = [];
    },
    UPDATE_EVENT(state, updated) {
      const index = state.events.findIndex((e) => matchEvent(e, updated));
      if (index === -1) return;

      state.events.splice(index, 1, {
        ...updated,
        customer: state.events[index].customer,
      });
    },
    ADD_OR_UPDATE_EVENT_INVITATION(state, { eventId, invitation }) {
      state.events.forEach((event, index) => {
        if (event.id !== eventId) return;

        const exists = event.invitations.some(
          (inv) => inv.id === invitation.id
        );
        const invitations = exists
          ? event.invitations.map((inv) =>
              inv.id === invitation.id ? invitation : inv
            )
          : [...event.invitations, invitation];

        state.events.splice(index, 1, { ...event, invitations });
      });
    },
    REMOVE_EVENT_INVITATION(state, { eventId, invitationId }) {
      state.events.forEach((event, index) => {
        if (event.id !== eventId) return;

        const invitations = event.invitations.filter(
          (inv) => inv.id !== invitationId
        );
        state.events.splice(index, 1, { ...event, invitations });
      });
    },
    ADD_OR_UPDATE_EVENT_COMMENT(state, { eventId, comment }) {
      state.events.forEach((event, index) => {
        const seriesId = event.parentId ?? event.id;
        if (seriesId !== eventId) return;

        const exists = event.comments?.some((c) => c.id === comment.id);
        const comments = exists
          ? event.comments.map((c) => (c.id === comment.id ? comment : c))
          : [...(event.comments ?? []), comment];

        state.events.splice(index, 1, { ...event, comments });
      });
    },
    REMOVE_EVENT_COMMENT(state, { eventId, commentId }) {
      state.events.forEach((event, index) => {
        const seriesId = event.parentId ?? event.id;
        if (seriesId !== eventId) return;

        const comments = event.comments.filter((c) => c.id !== commentId);
        state.events.splice(index, 1, { ...event, comments });
      });
    },
    SET_PLANNER_EVENTS(state, events) {
      state.plannerEvents = events;
    },
    CLEAR_PLANNER_EVENTS(state) {
      state.plannerEvents = [];
    },
    SET_EVENT_TYPES(state, eventTypes) {
      state.eventTypes = eventTypes;
    },
    SET_EVENT_HISTORY(state, eventHistory) {
      state.eventHistory = eventHistory;
    },
    CLEAR_EVENT_HISTORY(state) {
      state.eventHistory = [];
    },
    SET_PROSPECTS(state, prospects) {
      state.prospects = prospects;
    },
    CLEAR_PROSPECTS(state) {
      state.prospects = [];
    },
    SET_CUSTOMERS(state, customers) {
      state.customers = customers;
    },
    CLEAR_CUSTOMERS(state) {
      state.customers = [];
    },
    SET_RELATED_USERS(state, relatedUsers) {
      state.relatedUsers = relatedUsers;
    },
    CLEAR_RELATED_USERS(state) {
      state.relatedUsers = [];
    },
    SET_SELECTED_USER_ID(state, userId) {
      state.selectedUserId = userId;
    },
    SET_SELECTED_CHANNEL(state, channel) {
      state.selectedChannel = channel;
    },
    SET_CURRENT_RANGE(state, { from, to }) {
      state.currentRange = { from, to };
    },
    SET_IS_LOADING_EVENTS(state, isLoading) {
      state.isLoadingEvents = isLoading;
    },
    SET_IS_LOADING_PLANNER_EVENTS(state, isLoading) {
      state.isLoadingPlannerEvents = isLoading;
    },
    SET_IS_LOADING_EVENT_TYPES(state, isLoading) {
      state.isLoadingEventTypes = isLoading;
    },
    SET_IS_LOADING_EVENT_HISTORY(state, isLoading) {
      state.isLoadingEventHistory = isLoading;
    },
    SET_IS_LOADING_PROSPECTS(state, isLoading) {
      state.isLoadingProspects = isLoading;
    },
    SET_IS_LOADING_CUSTOMERS(state, isLoading) {
      state.isLoadingCustomers = isLoading;
    },
    SET_IS_LOADING_RELATED_USERS(state, isLoading) {
      state.isLoadingRelatedUsers = isLoading;
    },
  },
  actions: {
    async getEventTypes({ commit }) {
      if (!SchedulePolicy.canList()) return;

      commit('SET_IS_LOADING_EVENT_TYPES', true);

      try {
        const data = await getEventTypes();

        commit('SET_EVENT_TYPES', data);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar los tipos de evento',
        };
      } finally {
        commit('SET_IS_LOADING_EVENT_TYPES', false);
      }
    },
    async getUserEvents(
      { commit, dispatch, rootGetters },
      { userId, from, to }
    ) {
      if (!SchedulePolicy.canList() || !userId) return;

      commit('CLEAR_EVENTS');
      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const [data] = await Promise.all([
          getUserEvents(userId, { from, to }),
          dispatch(
            'scheduleNotification/loadDescendants',
            {
              userId: rootGetters['auth/user']?.id,
              channel: rootGetters['auth/user']?.VTWEG,
            },
            { root: true }
          ),
        ]);

        const events = await enrichEventsWithCustomerNames(
          data,
          rootGetters['scheduleNotification/customersFromDescendants']
        );

        commit('SET_EVENTS', events);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };
        return { data: null, error: 'Error al cargar los eventos del usuario' };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },

    async getUserPlannerEvents({ commit }, { userId, from, to }) {
      if (!PlannerPolicy.canList() || !userId) return;

      commit('CLEAR_PLANNER_EVENTS');
      commit('SET_IS_LOADING_PLANNER_EVENTS', true);

      try {
        const data = await getUserPlannerEvents(userId, { from, to });
        commit('SET_PLANNER_EVENTS', data);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar los eventos del planeador',
        };
      } finally {
        commit('SET_IS_LOADING_PLANNER_EVENTS', false);
      }
    },
    async createUserEvent({ commit, dispatch, getters }, { userId, event }) {
      if (!SchedulePolicy.canCreate() || !userId) return;

      commit('SET_IS_LOADING_EVENTS', true);
      try {
        const data = await createUserEvent(userId, event);

        const { from, to } = getters.currentRange;

        await Promise.all([
          dispatch('getUserEvents', { userId, from, to }),
          dispatch('scheduleNotification/loadTodayEvents', null, {
            root: true,
          }),
        ]);

        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error: err?.response?.data?.message || 'Error al crear el evento',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async updateUserEvent(
      { commit, dispatch, getters },
      { userId, eventId, event, action }
    ) {
      if (!SchedulePolicy.canUpdate() || !userId || !eventId) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const data = await updateUserEvent(eventId, event);

        if (action) {
          await dispatch('createEventAction', { eventId: data?.id, action });
        }

        const { from, to } = getters.currentRange;

        await Promise.all([
          dispatch('getUserEvents', { userId, from, to }),
          dispatch('scheduleNotification/loadTodayEvents', null, {
            root: true,
          }),
        ]);

        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error:
            err?.response?.data?.message || 'Error al actualizar el evento',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async syncUpdatedEvent({ commit }, event) {
      commit('UPDATE_EVENT', event);
      commit('scheduleNotification/UPDATE_EVENT', event, { root: true });
    },
    async createEventInvitation(
      { commit, dispatch },
      { userId, eventId, email, guestId }
    ) {
      if (!SchedulePolicy.canUpdate() || !userId || !eventId || !email) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const { data, message } = await createInvitation({
          userId,
          type: InvitationType.ScheduleEvent,
          modelId: eventId,
          email,
          guestId,
        });

        commit('ADD_OR_UPDATE_EVENT_INVITATION', { eventId, invitation: data });
        commit(
          'scheduleNotification/ADD_OR_UPDATE_EVENT_INVITATION',
          { eventId, invitation: data },
          { root: true }
        );

        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data, message, error: null };
      } catch (err) {
        return {
          data: null,
          message: null,
          error: err?.response?.data?.message || 'Error al crear la invitación',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async revokeEventInvitation(
      { commit, dispatch },
      { eventId, invitationId }
    ) {
      if (!SchedulePolicy.canUpdate() || !invitationId) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const { data, message } = await revokeInvitation(invitationId);

        commit('ADD_OR_UPDATE_EVENT_INVITATION', { eventId, invitation: data });
        commit(
          'scheduleNotification/ADD_OR_UPDATE_EVENT_INVITATION',
          { eventId, invitation: data },
          { root: true }
        );
        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data: data, message, error: null };
      } catch (err) {
        return {
          data: null,
          message: null,
          error:
            err?.response?.data?.message || 'Error al eliminar la invitación',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async createEventExecution(
      { commit, dispatch },
      { eventId, execution, action }
    ) {
      if (!SchedulePolicy.canUpdate() || !eventId) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const data = await createEventExecution(eventId, execution);

        if (action) {
          await dispatch('createEventAction', { eventId, action });
        }

        dispatch('syncUpdatedEvent', data.event);
        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error: err?.response?.data?.message || 'Error al ejecutar el evento',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async updateEventExecution(
      { commit, dispatch },
      { eventId, execution, action }
    ) {
      if (!SchedulePolicy.canUpdate() || !eventId) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const data = await updateEventExecution(eventId, execution);

        if (action) {
          await dispatch('createEventAction', { eventId, action });
        }

        dispatch('syncUpdatedEvent', data);
        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error:
            err?.response?.data?.message || 'Error al actualizar el evento',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async createEventComment({ commit, dispatch }, { eventId, comment }) {
      if (!SchedulePolicy.canUpdate() || !eventId) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const data = await createEventComment(eventId, comment);

        commit('ADD_OR_UPDATE_EVENT_COMMENT', { eventId, comment: data });
        commit(
          'scheduleNotification/ADD_OR_UPDATE_EVENT_COMMENT',
          { eventId, comment: data },
          { root: true }
        );
        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error:
            err?.response?.data?.message || 'Error al publicar el comentario',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async updateEventComment(
      { commit, dispatch },
      { eventId, commentId, text, newFiles, removedImageIds }
    ) {
      if (!SchedulePolicy.canUpdate() || !eventId || !commentId) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        if (removedImageIds?.length) {
          await Promise.all(
            removedImageIds.map((id) =>
              deleteEventCommentEvidence(commentId, id)
            )
          );
        }

        if (newFiles?.length) {
          await addEventCommentEvidence(eventId, commentId, newFiles);
        }

        const data = await updateEventComment(eventId, commentId, text);

        commit('ADD_OR_UPDATE_EVENT_COMMENT', { eventId, comment: data });
        commit(
          'scheduleNotification/ADD_OR_UPDATE_EVENT_COMMENT',
          { eventId, comment: data },
          { root: true }
        );
        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error:
            err?.response?.data?.message || 'Error al actualizar el comentario',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async deleteEventComment({ commit, dispatch }, { eventId, commentId }) {
      if (!SchedulePolicy.canUpdate() || !eventId || !commentId) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        await deleteEventComment(eventId, commentId);

        commit('REMOVE_EVENT_COMMENT', { eventId, commentId });
        commit(
          'scheduleNotification/REMOVE_EVENT_COMMENT',
          { eventId, commentId },
          { root: true }
        );
        dispatch('scheduleNotification/notifyUpdate', null, {
          root: true,
        });

        return { data: null, error: null };
      } catch (err) {
        return {
          data: null,
          error:
            err?.response?.data?.message || 'Error al publicar el comentario',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async createEventAction({ commit }, { eventId, action }) {
      if (!SchedulePolicy.canUpdate() || !eventId || !action) return;

      commit('SET_IS_LOADING_EVENTS', true);

      try {
        const data = await createEventAction(eventId, action);
        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error: err?.response?.data?.message || 'Error al guardar la acción',
        };
      } finally {
        commit('SET_IS_LOADING_EVENTS', false);
      }
    },
    async getUserEventHistory(
      { commit, dispatch, rootGetters },
      { userId, from, to }
    ) {
      if (!SchedulePolicy.canList() || !userId) return;

      commit('CLEAR_EVENT_HISTORY');
      commit('SET_IS_LOADING_EVENT_HISTORY', true);

      try {
        const [data] = await Promise.all([
          getUserEvents(userId, { from, to, executed: true }),
          dispatch(
            'scheduleNotification/loadDescendants',
            {
              userId: rootGetters['auth/user']?.id,
              channel: rootGetters['auth/user']?.VTWEG,
            },
            { root: true }
          ),
        ]);

        const events = await enrichEventsWithCustomerNames(
          data,
          rootGetters['scheduleNotification/customersFromDescendants']
        );

        commit('SET_EVENT_HISTORY', events);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar el historial de eventos del usuario',
        };
      } finally {
        commit('SET_IS_LOADING_EVENT_HISTORY', false);
      }
    },
    async getUserCustomers({ commit }, { userId, channel }) {
      if (!SchedulePolicy.canList() || !userId || !channel) return;

      commit('CLEAR_CUSTOMERS');
      commit('SET_IS_LOADING_CUSTOMERS', true);

      try {
        const data = await getUserCustomers(userId, channel);

        commit('SET_CUSTOMERS', data);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar los clientes del usuario',
        };
      } finally {
        commit('SET_IS_LOADING_CUSTOMERS', false);
      }
    },
    async getCustomerInfo(context, { customerCode }) {
      try {
        const data = await getCustomerInfo(customerCode);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar la información del cliente',
        };
      }
    },
    async getUserRelatedUsers({ commit }, { userId }) {
      if (!SchedulePolicy.canList() || !userId) return;

      commit('CLEAR_RELATED_USERS');
      commit('SET_IS_LOADING_RELATED_USERS', true);

      try {
        const data = await getUserRelatedUsers(userId);

        commit('SET_RELATED_USERS', data);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar la estructura comercial del usuario',
        };
      } finally {
        commit('SET_IS_LOADING_RELATED_USERS', false);
      }
    },
    async getUserProspects({ commit }, { userId }) {
      if (!ProspectPolicy.canList() || !userId) return;

      commit('CLEAR_PROSPECTS');
      commit('SET_IS_LOADING_PROSPECTS', true);

      try {
        const data = await getUserProspects(userId);

        commit('SET_PROSPECTS', data);
        return { data, error: null };
      } catch (err) {
        if (err?.code === 'ERR_CANCELED') return { data: null, error: null };

        return {
          data: null,
          error: 'Error al cargar los prospectos del usuario',
        };
      } finally {
        commit('SET_IS_LOADING_PROSPECTS', false);
      }
    },
    async createUserProspect({ commit, state }, { userId, prospect }) {
      if (!ProspectPolicy.canCreate() || !userId) return;

      commit('SET_IS_LOADING_PROSPECTS', true);

      try {
        const data = await createUserProspect(userId, prospect);

        commit('SET_PROSPECTS', [...state.prospects, data]);
        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error: 'Error al crear el prospecto',
        };
      } finally {
        commit('SET_IS_LOADING_PROSPECTS', false);
      }
    },
    async updateUserProspect({ commit, state }, { prospectId, prospect }) {
      if (!ProspectPolicy.canUpdate(prospect) || !prospectId) return;

      commit('SET_IS_LOADING_PROSPECTS', true);

      try {
        const data = await updateUserProspect(prospectId, prospect);
        const updatedProspects = state.prospects.map((p) =>
          p.id === prospectId ? data : p
        );

        commit('SET_PROSPECTS', updatedProspects);
        return { data, error: null };
      } catch (err) {
        return {
          data: null,
          error: 'Error al modificar el prospecto',
        };
      } finally {
        commit('SET_IS_LOADING_PROSPECTS', false);
      }
    },
    async deleteUserProspect({ commit, state }, { prospect }) {
      if (!ProspectPolicy.canDelete(prospect) || !prospect?.id) return;

      commit('SET_IS_LOADING_PROSPECTS', true);

      try {
        await deleteUserProspect(prospect.id);
        const updatedProspects = state.prospects.filter(
          (p) => p.id !== prospect.id
        );

        commit('SET_PROSPECTS', updatedProspects);
        return { error: null };
      } catch (err) {
        return {
          data: null,
          error: 'Error al eliminar el prospecto',
        };
      } finally {
        commit('SET_IS_LOADING_PROSPECTS', false);
      }
    },
  },
};
